import apollo from '@/graphql'

import SaveStoreDetails from '@/graphql/store/SaveStoreDetails.gql'
import SaveStoreInstallFields from '@/graphql/store/SaveStoreInstallFields.gql'
import SaveStoreInstallLanes from '@/graphql/store/SaveStoreInstallLanes.gql'
import SaveStoreNote from '@/graphql/store_note/SaveStoreNote.gql'
import SaveStoreWifi from '@/graphql/store/SaveStoreWifi.gql'
import SetStoreInstalled from '@/graphql/store/SetStoreInstalled.gql'
import StoreDetailsQuery from '@/graphql/store/StoreDetailsQuery.gql'
import UploadStoreImage from '@/graphql/store/UploadStoreImage.gql'
import DeleteStoreImage from '@/graphql/store/DeleteStoreImage.gql'
import StoreClustersQuery from '@/graphql/store/StoreClustersQuery.gql'
import TakeAllDevicesPictures from '@/graphql/device/TakeAllDevicesPictures.gql'
import TakeDevicePicture from '@/graphql/device/TakeDevicePicture.gql'
import TakeLaneSidePicture from '@/graphql/lane_side/TakeLaneSidePicture.gql'
import UnassignedDevicesQuery from '@/graphql/device/UnassignedDevicesQuery.gql'
import SaveAssignedRep from '@/graphql/device_issue/SaveAssignedRep.gql'
import SaveDeviceAssign from '@/graphql/device/SaveDeviceAssign.gql'

// Hotspot
import HotspotStatusListQuery from '@/graphql/store/HotspotStatusListQuery.gql'
import HotspotListQuery from '@/graphql/store/HotspotListQuery.gql'
import HotspotDetailsQuery from '@/graphql/store/HotspotDetailsQuery.gql'
import StoreHotspotDetailsQuery from '@/graphql/store/StoreHotspotDetailsQuery.gql'
import SaveHotspotQuery from '@/graphql/store/SaveHotspot.gql'
import SaveHotspotAssignment from '@/graphql/store/SaveHotspotAssignment.gql'

// initial state
const state = {
  editableHotspotFields: {},
  editableStoreHotspotDetails: {},
  editableStoreFields: {},
  isStoreDetailsLoading: false,
  isStoreDetailsSaving: false,
  lanes: [],
  storeClusters: [],
  storeDetails: {},
  storeClosedIssues: [], // Backend only returns the last 100 closed issues
  storeOpenIssues: [],
  isModifiedDeviceIssues: false,
  hotspotStatusList: [],
  hotspotList: [],
  hotspotDetails: {
    id: '',
    name: '',
    status: '',
    ssid: '',
    password: '',
    imei: '',
    simSerialNumber: '',
    deviceSerialNumber: '',
    adminPSK: '',
    mobileNumber: '',
    createdAt: '',
    createdBy: '',
    model: '',
  },
  storeHotspotDetails: {
    id: '',
    name: '',
    notes: '',
  },
}

// getters
const getters = {
  editableHotspotFields: st => st.editableHotspotFields,
  editableStoreHotspotDetails: st => st.editableStoreHotspotDetails,
  editableStoreFields: st => st.editableStoreFields,
  isStoreDetailsLoading: st => st.isStoreDetailsLoading,
  isStoreDetailsSaving: st => st.isStoreDetailsSaving,
  lanes: st => st.lanes,
  storeClusters: st => st.storeClusters,
  storeDetails: st => st.storeDetails,
  storeClosedIssues: st => st.storeClosedIssues,
  storeOpenIssues: st => st.storeOpenIssues,
  isModifiedDeviceIssues: st => st.isModifiedDeviceIssues,
  hotspotStatusList: st => st.hotspotStatusList,
  hotspotList: st => st.hotspotList,
  hotspotDetails: st => st.hotspotDetails,
  storeHotspotDetails: st => st.storeHotspotDetails,
}

// mutations
const mutations = {
  addLane() {
    state.lanes.push({
      devicesId: null,
      leftLaneSide: { isHidden: false },
      number: state.lanes.filter(lane => !lane.isDeleted).length + 1,
      rightLaneSide: { isHidden: false },
      isBeltLeft: true,
      isNumbered: true,
      position: state.lanes.filter(lane => !lane.isDeleted).length,
      isDeleted: false,
    })
  },

  reverseLaneOrder() {
    const numLanes = state.lanes.length - 1
    const reversedLanes = []
    state.lanes.forEach((lane) => {
      const modifiedLane = lane
      modifiedLane.position = Math.abs(lane.position - numLanes)
      reversedLanes.push(modifiedLane)
    })
  },

  createStoreDetails() {
    state.editableStoreFields = {
      hours: {
        global: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Mon: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Tue: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Wed: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Thu: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Fri: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Sat: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
        Sun: { isEnabled: true, open: 0, close: 3.6e6 * 24 },
      },
      isActive: true,
      isAtmosphereEnabled: true,
      isDhcp: true,
      isFillerEnabled: true,
      isHighPriority: false,
      isInstalled: false,
      isProgrammatic: true,
      isPassiveProgrammatic: false,
      isSpanishEmails: false,
      networkSecurityType: 'WPA',
      psk: null,
      wpaePassword: null,
      userNotifications: [],
    }

    state.lanes = []

    state.storeDetails = {
      lanes: [],
      name: 'New Store',
      retailer: {},
    }

    state.storeOpenIssues = []
    state.storeClosedIssues = []
  },

  async removeLane(st, { lane }) {
    const index = state.lanes.indexOf(lane)
    state.lanes[index].isDeleted = !state.lanes[index].isDeleted
  },

  setEditableStoreFields(st, storeDetails) {
    state.editableStoreFields = {
      address: storeDetails.address,
      bannersId: storeDetails.banner.id,
      city: storeDetails.city,
      gateway: storeDetails.gateway,
      hours: JSON.parse(storeDetails.hours),
      id: storeDetails.id,
      installDate: storeDetails.installDate,
      installerNotes: storeDetails.installerNotes,
      isActive: storeDetails.isActive,
      isAtmosphereEnabled: storeDetails.isAtmosphereEnabled,
      isDhcp: storeDetails.isDhcp,
      isExpectWifi: storeDetails.isExpectWifi,
      isFillerEnabled: storeDetails.isFillerEnabled,
      isHighPriority: storeDetails.isHighPriority,
      isInstalled: storeDetails.isInstalled,
      isInstallerWifiTest: storeDetails.isInstallerWifiTest,
      isProgrammatic: storeDetails.isProgrammatic,
      isPassiveProgrammatic: storeDetails.isPassiveProgrammatic,
      isSendSCReports: storeDetails.isSendSCReports,
      isSpanishEmails: storeDetails.isSpanishEmails,
      name: storeDetails.name,
      networkPrefixLength: storeDetails.networkPrefixLength,
      networkSecurityType: storeDetails.networkSecurityType || 'WPA', // if null, set to WPA
      wpaEnterpriseUsername: storeDetails.wpaEnterpriseUsername,
      notes: storeDetails.notes,
      phone: storeDetails.phone,
      psk: storeDetails.psk,
      wpaePassword: storeDetails.wpaePassword,
      retailersId: storeDetails.retailer.id,
      scFrequency: storeDetails.scFrequency,
      ssid: storeDetails.ssid,
      state: storeDetails.state,
      tz: storeDetails.tz,
      uninstallDate: storeDetails.uninstallDate,
      userNotifications: storeDetails.userNotifications.map(userNotification => ({
        emailField: userNotification.emailField,
        notificationsId: userNotification.notificationsId,
        usersId: userNotification.usersId,
        usersName: userNotification.user.name,
      })),
      zip: storeDetails.zip,
      opsManagerId: storeDetails.opsManagerId,
      isAssignedOver72Hours: storeDetails.isAssignedOver72Hours,
      storeClusterId: storeDetails.storeClusterId,
      internetType: storeDetails.internetType,
    }
  },

  async setLanes(st, storeDetails) {
    state.lanes = await storeDetails.lanes.map(lane => ({
      id: lane.id,
      ipAddress: lane.ipAddress,
      number: lane.number,
      leftLaneSide: {
        id: lane.leftLaneSide.id,
        isHidden: lane.leftLaneSide.isHidden,
      },
      rightLaneSide: {
        id: lane.rightLaneSide.id,
        isHidden: lane.rightLaneSide.isHidden,
      },
      isBeltLeft: lane.leftLaneSide.isBelt,
      isNumbered: lane.isNumbered,
      devicesId: `${lane.device.id || ''}`, // String
      position: lane.position,
      wifiSSID: lane.device.wifiSSID,
      isDeleted: lane.isDeleted,
    }))
  },

  setStoreClusters(st, storeClusters) {
    state.storeClusters = storeClusters
  },

  setStoreDetails(st, storeDetails) {
    state.storeDetails = storeDetails
  },

  setStoreIssues(st, storeDetails) {
    const openIssues = []
    const closedIssues = []
    storeDetails.lanes.forEach((lane) => {
      if (lane.device.openIssues.length > 0) {
        openIssues.push(
          ...lane.device.openIssues.map(issue => ({
            ...issue,
            ...{ laneNumber: lane.number, subtypeId: issue.subtype.id },
          })),
        )
      }
      if (lane.device.closedIssues.length > 0) {
        closedIssues.push(
          ...lane.device.closedIssues.map(issue => ({
            ...issue,
            ...{ laneNumber: lane.number, subtypeId: issue.subtype.id },
          })),
        )
      }
    })
    state.storeOpenIssues = openIssues
    state.storeClosedIssues = closedIssues
  },

  setIsModifiedDeviceIssues(st, value) {
    state.isModifiedDeviceIssues = value
  },

  setIsStoreDetailsLoadingState(st, value) {
    state.isStoreDetailsLoading = value
  },

  updateStoreFields(st, fields) {
    Object.assign(state.editableStoreFields, fields)
  },

  /** HOTSPOT RELATED FUNCTIONS */
  setEditableHotspotFields(st, hotspotDetails) {
    state.editableHotspotFields = {
      id: hotspotDetails.id,
      name: hotspotDetails.name,
      status: hotspotDetails.status,
      ssid: hotspotDetails.ssid,
      password: hotspotDetails.password,
      imei: hotspotDetails.imei,
      simSerialNumber: hotspotDetails.simSerialNumber,
      deviceSerialNumber: hotspotDetails.deviceSerialNumber,
      adminPSK: hotspotDetails.adminPSK,
      mobileNumber: hotspotDetails.mobileNumber,
      createdAt: hotspotDetails.createdAt,
      createdBy: hotspotDetails.createdBy,
      storeId: hotspotDetails.storeId,
      model: hotspotDetails.model,
    }
  },

  setEditableStoreHotspotDetails(st, storeHotspotDetails) {
    state.editableStoreHotspotDetails = {
      id: storeHotspotDetails.id,
      name: storeHotspotDetails.name,
      notes: storeHotspotDetails.notes,
    }
  },

  setHotspotStatusList(st, value) {
    state.hotspotStatusList = value
  },

  setHotspotList(st, value) {
    state.hotspotList = value
  },

  setHotspotDetails(st, value) {
    state.hotspotDetails = value
  },

  setStoreHotspotDetails(st, value) {
    state.storeHotspotDetails = value
  },
  /** END HOTSPOT RELATED FUNCTIONS */
}

// actions
const actions = {
  async loadStoreClusters({ commit }) {
    return apollo.defaultClient.watchQuery({
      fetchPolicy: 'network-only',
      query: StoreClustersQuery,
    }).subscribe(({ data: { StoreClusters } }) => {
      commit('setStoreClusters', StoreClusters)
    })
  },

  async loadStoreDetails(
    { commit },
    { id, initialLoad, keepEditableFields, $message, showSaved },
  ) {
    commit('setIsStoreDetailsLoadingState', true)

    if (initialLoad) {
      Object.assign(state, {
        editableStoreFields: {},
        lanes: [],
        storeDetails: {},
      })
    }

    const {
      data: { storeDetails },
    } = await apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: StoreDetailsQuery,
      variables: { id: +id },
    })

    commit('setStoreDetails', storeDetails)

    if (!keepEditableFields) {
      commit('setEditableStoreFields', storeDetails)
      commit('setLanes', storeDetails)
      commit('setStoreIssues', storeDetails)
    }

    if (showSaved) {
      $message({ message: 'Changes saved', type: 'success' })
      state.isStoreDetailsSaving = false
    }
    commit('setIsStoreDetailsLoadingState', false)
  },

  async loadHotspotStatusList({ commit }) {
    await apollo.defaultClient.query({
      query: HotspotStatusListQuery,
    }).then(({ data: { getHotspotStatusList } }) => {
      commit('setHotspotStatusList', getHotspotStatusList)
    })
  },

  async loadHotspotList({ commit }, { isUnassigned }) {
    await apollo.defaultClient.query({
      query: HotspotListQuery,
      variables: { isUnassigned },
    }).then(({ data: { getHotspotList } }) => {
      commit('setHotspotList', getHotspotList)
    })
  },

  async loadHotspotDetails({ commit }, { id }) {
    if (id) {
      await apollo.defaultClient.query({
          query: HotspotDetailsQuery,
          variables: { id: +id, storeId: +state.storeDetails.id },
        }).then(({ data: { getHotspotDetails } }) => {
          commit('setHotspotDetails', getHotspotDetails)
          commit('setEditableHotspotFields', getHotspotDetails)
          return getHotspotDetails
        })
    } else {
      commit('setHotspotDetails', {})
    }
  },

  async loadStoreHotspotDetails({ commit }, { storeId }) {
    if (storeId) {
      await apollo.defaultClient.query({
          query: StoreHotspotDetailsQuery,
          variables: { storeId: +storeId },
        }).then(({ data: { getStoreHotspotDetails } }) => {
          commit('setStoreHotspotDetails', getStoreHotspotDetails)
          commit('setEditableStoreHotspotDetails', getStoreHotspotDetails)
          return getStoreHotspotDetails
        })
    } else {
      commit('setStoreHotspotDetails', {})
    }
  },

  async saveHotspot({ dispatch }, { fields, $message }) {
    await apollo.defaultClient.mutate({
      mutation: SaveHotspotQuery,
      variables: { fields },
    }).then(({ data: { saveHotspot } }) => {
      $message({ message: 'Changes saved', type: 'success' })
      state.hotspotList.push(saveHotspot)
    })
    .finally(() => {
      dispatch('loadHotspotList', false)
    })
  },

  async saveHotspotAssignment({ dispatch }, { fields, $message }) {
    return apollo.defaultClient.mutate({
      mutation: SaveHotspotAssignment,
      variables: { fields },
    }).then(() => {
      $message({ message: 'Changes saved', type: 'success' })
      dispatch('loadStoreDetails', {
        id: state.storeDetails.id,
        keepEditableFields: true,
      })
    })
  },

  revertHotspotDetailsEdits({
    commit,
    state: {
      hotspotDetails,
      storeHotspotDetails,
    },
  }) {
    commit('setEditableHotspotFields', hotspotDetails)
    commit('setEditableStoreHotspotDetails', storeHotspotDetails)
  },

  async saveStoreDetails({
    commit,
    dispatch,
    state: {
      editableStoreFields,
      lanes,
      storeDetails,
    },
  }, {
    $confirm,
    $message,
    $router,
    isReloadPage,
    activeTab,
    removalReason,
    removalNote,
    userId,
  }) {
    const removedLanes = storeDetails.lanes.filter(
      lane => !lanes.find(l => (l.id === lane.id)),
    )

    if (removedLanes.length) {
      try {
        await $confirm('Are you sure you want to delete these lanes?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning',
        })
      } catch (err) {
        /**
         * Revert all changes.
         */
        commit('setEditableStoreFields', storeDetails)
        commit('setLanes', storeDetails)
        return
      }
    }

    state.isStoreDetailsSaving = true
    try {
      const { data: { saveStoreDetails: { id } } } = await apollo.defaultClient
        .mutate({
          mutation: SaveStoreDetails,
          variables: {
            fields: Object.assign({}, editableStoreFields, {
              /**
               * `installDate` expects a String, but sometimes is an epoch long.
               */
              installDate: String(editableStoreFields.installDate),
              uninstallDate: String(editableStoreFields.uninstallDate),
              removalReason,
              removalNote,
              userId,
              method: storeDetails.method,
            }),
            lanes: lanes.map(lane => Object.assign(lane, { number: +(lane.number) })),
          },
          refetchQueries: [{ query: UnassignedDevicesQuery }],
        })

      if (isReloadPage) {
        $router.push(`/store/${id}/${activeTab || ''}`)
      }

      if (storeDetails.id || id) {
        await dispatch('loadStoreDetails', {
          id: storeDetails.id ? storeDetails.id : id,
          $message,
          showSaved: true,
        })
      } else {
        $message({ message: 'Changes saved', type: 'success' })
      }
      /* eslint-disable-next-line consistent-return */
      return id
    } catch (err) {
        state.isStoreDetailsSaving = false
        await dispatch('revertStoreDetailsEdits')

        $message.error({
          duration: 0,
          message: err.message,
          showClose: true,
        })
      }
  },

  saveDeviceAssign(root, variables) {
    return apollo.defaultClient
      .mutate({
        mutation: SaveDeviceAssign,
        variables,
      })
  },

  takeDevicePicture({ dispatch }, { variables }) {
    return apollo.defaultClient
      .mutate({
        mutation: TakeDevicePicture,
        variables,
      })
      .then(() => {
        dispatch('loadStoreDetails', {
          id: state.storeDetails.id,
          keepEditableFields: true,
        })
      })
  },

  takeAllDevicesPictures({ dispatch }, { variables }) {
    return apollo.defaultClient
      .mutate({
        mutation: TakeAllDevicesPictures,
        variables,
      })
      .then(() => {
        dispatch('loadStoreDetails', {
          id: state.storeDetails.id,
          keepEditableFields: true,
        })
      })
  },

  takeLaneSidePicture({ dispatch }, { laneSidesId }) {
    return apollo.defaultClient
      .mutate({
        mutation: TakeLaneSidePicture,
        variables: { laneSidesId },
      })
      .then(() => {
        dispatch('loadStoreDetails', {
          id: state.storeDetails.id,
          keepEditableFields: true,
        })
      })
  },

  revertStoreDetailsEdits({
    commit,
    state: {
      storeDetails,
    },
  }) {
    commit('setEditableStoreFields', storeDetails)
    commit('setLanes', storeDetails)
    commit('setStoreIssues', storeDetails)
  },
  async saveStoreNote(context, { $message, note, noMessage }) {
    const assignRepToIssue = (issueId, repId) => {
      apollo.defaultClient.mutate({
        mutation: SaveAssignedRep,
        variables: {
          fields: {
            id: issueId,
            assignedRep: repId === 1 // FIXME: Magic number
              ? null // Make null when unassigned
              : repId,
          },
        },
      })
        .catch((err) => {
          throw new Error(`Failed to assign issue ID ${issueId}: ${err.message}`)
        })
    }

    const openIssues = state.storeOpenIssues
      .filter(_ => _.assignedRepUser.id !== state.storeDetails.opsManagerId)
    if (note.isEscalated && state.storeDetails.opsManagerId && openIssues.length > 0) {
      try {
        await Promise.all([
          // eslint-disable-next-line arrow-body-style
          ...openIssues.map((issue) => {
            return assignRepToIssue(
              issue.id,
              state.storeDetails.opsManagerId,
            )
          }),
        ])

        $message({
          message: `Assignment updated for ${state.storeDetails.name}`,
          type: 'success',
        })
      } catch (err) {
        $message({
          message: err.message,
          type: 'error',
        })
      }
    }

    return apollo.defaultClient
      .mutate({
        mutation: SaveStoreNote,
        variables: { fields: note },
        refetchQueries: {
          query: StoreDetailsQuery,
          variables: { id: state.storeDetails.id },
        },
      })
      .then(() => {
        if (!noMessage) {
          $message({
            message: `Store Issue Note ${note.isDelete ? 'Deleted' : 'Saved'}`,
            type: 'success',
          })
        }
      })
      .catch((err) => {
        $message({
          message: err,
          type: 'error',
        })
      })
  },

  async saveStoreWifi({ dispatch }, { $message }) {
    apollo.defaultClient
      .mutate({
        mutation: SaveStoreWifi,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            wpaEnterpriseUsername: state.editableStoreFields.wpaEnterpriseUsername,
            networkSecurityType: state.editableStoreFields.networkSecurityType,
            psk: state.editableStoreFields.psk,
            wpaePassword: state.editableStoreFields.wpaePassword,
            ssid: state.editableStoreFields.ssid,
          },
        },
      })
      .then(({ data: { saveStoreWifi: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      }).catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async saveStoreInstallFields({ dispatch }, { $message }) {
    await apollo.defaultClient
      .mutate({
        mutation: SaveStoreInstallFields,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            isInstallerWifiTest: state.editableStoreFields.isInstallerWifiTest,
          },
        },
      }).then(({ data: { saveStoreInstallFields: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async saveStoreInstallLanes({ commit, dispatch }, { $confirm, $message }) {
    const removedLanes = state.storeDetails.lanes.filter(
      lane => !state.lanes.find(l => (l.id === lane.id)),
    )

    if (removedLanes.length) {
      try {
        await $confirm('Are you sure you want to delete these lanes?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning',
        })
      } catch (err) {
        // Revert all changes.
        commit('setEditableStoreFields', state.storeDetails)
        commit('setLanes', state.storeDetails)
      }
    }

    await apollo.defaultClient
      .mutate({
        mutation: SaveStoreInstallLanes,
        variables: {
          storeId: state.editableStoreFields.id,
          lanes: state.lanes.map(lane => Object.assign(lane, { number: +(lane.number) })),
        },
      }).then(({ data: { saveStoreInstallLanes: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  async setStoreInstalled({ dispatch }, { $message }) {
    await apollo.defaultClient
      .mutate({
        mutation: SetStoreInstalled,
        variables: {
          fields: {
            id: state.editableStoreFields.id,
            installDate: state.editableStoreFields.installDate,
            isInstalled: state.editableStoreFields.isInstalled,
            isAtmosphereEnabled: state.editableStoreFields.isAtmosphereEnabled,
          },
        },
      }).then(({ data: { setStoreInstalled: { id } } }) => {
        if (id) {
          dispatch('loadStoreDetails', {
            id,
            $message,
            showSaved: true,
          })
        } else {
          $message({ message: 'Changes saved', type: 'success' })
        }
      })
      .catch((err) => {
        state.isStoreDetailsSaving = false
        dispatch('revertStoreDetailsEdits')

        $message({
          message: err.message,
          type: 'error',
        })
      })
  },

  deleteStoreImage({ dispatch }, { id, $message }) {
    apollo.defaultClient.mutate({
      mutation: DeleteStoreImage,
      variables: { id: +id },
    }).then(() => {
      dispatch('loadStoreDetails', {
        id: state.storeDetails.id,
        $message,
        showSaved: true,
      })
    }).catch((err) => {
      $message.error(err.graphQLErrors[0].message)
    })
  },

  uploadStoreImage({ dispatch }, {
    fields, file, $message,
  }) {
    apollo.defaultClient.mutate({
      mutation: UploadStoreImage,
      variables: { file, storesId: fields.id },
    }).then(() => {
      dispatch('loadStoreDetails', {
        id: state.storeDetails.id,
        $message,
        showSaved: true,
      })
    }).catch((err) => {
      $message.error(err.graphQLErrors[0])
    })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/store/StoreDetails.js